import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

const LoginLogs = () => {
  const { currentLang, token, API_URL } = useAuth();
  
  const [allLogs, setAllLogs] = useState([]); // Все логи с сервера
  const [filteredLogs, setFilteredLogs] = useState([]); // Отфильтрованные логи
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для фильтров
  const [filters, setFilters] = useState({
    success: '', // '1' для успешных, '0' для неуспешных, '' для всех
    ip: '',
    date_from: '',
    date_to: '',
  });
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [limit] = useState(50);

  // Состояние для активного таба
  const [activeTab, setActiveTab] = useState('logs');

  // Загрузка данных
  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const url = `${API_URL}${currentLang}/login-logs?days=30`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setAllLogs(data.logs || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки логов');
      }
    } catch (error) {
      console.error('Ошибка загрузки логов:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Загружаем данные при монтировании компонента
  useEffect(() => {
    fetchLogs();
  }, []);

  // Фильтрация и пагинация на фронтенде
  useEffect(() => {
    let filtered = [...allLogs];

    // Фильтр по статусу
    if (filters.success !== '') {
      filtered = filtered.filter(log => log.success.toString() === filters.success);
    }

    // Фильтр по IP
    if (filters.ip) {
      filtered = filtered.filter(log => 
        log.ip && log.ip.toLowerCase().includes(filters.ip.toLowerCase())
      );
    }

    // Фильтр по датам
    if (filters.date_from) {
      const fromDate = new Date(filters.date_from);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.datetime);
        return logDate >= fromDate;
      });
    }

    if (filters.date_to) {
      const toDate = new Date(filters.date_to);
      toDate.setHours(23, 59, 59, 999); // Конец дня
      filtered = filtered.filter(log => {
        const logDate = new Date(log.datetime);
        return logDate <= toDate;
      });
    }

    // Сортировка по дате (новые сначала)
    filtered.sort((a, b) => new Date(b.datetime) - new Date(a.datetime));

    setFilteredLogs(filtered);
    setTotalLogs(filtered.length);
    setTotalPages(Math.ceil(filtered.length / limit));
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  }, [allLogs, filters, limit]);

  // Получаем текущую страницу данных
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredLogs.slice(startIndex, endIndex);
  };

  // Обработчики фильтров
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Очистка фильтров
  const clearFilters = () => {
    setFilters({
      success: '',
      ip: '',
      date_from: '',
      date_to: '',
    });
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('ru-RU');
    } catch (error) {
      return dateString;
    }
  };

  // Получение статуса авторизации
  const getSuccessText = (success) => {
    return success === 1 
      ? (currentLang === 'ru' ? 'Успешно' : 'Success')
      : (currentLang === 'ru' ? 'Неуспешно' : 'Failed');
  };

  const getSuccessColor = (success) => {
    return success === 1 
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  // Сокращение User Agent
  const shortenUserAgent = (userAgent) => {
    if (!userAgent) return '-';
    if (userAgent.length <= 50) return userAgent;
    return userAgent.substring(0, 50) + '...';
  };

  // Статистика
  const getStats = () => {
    const total = allLogs.length;
    const successful = allLogs.filter(log => log.success === 1).length;
    const failed = allLogs.filter(log => log.success === 0).length;
    const successRate = total > 0 ? Math.round((successful / total) * 100) : 0;

    return { total, successful, failed, successRate };
  };

  const stats = getStats();

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Заголовок */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white break-words">
                  {currentLang === 'ru' ? 'Журнал авторизаций' : 'Login Logs'}
                </h1>
                <p className="text-blue-100 mt-1 sm:mt-2 text-sm sm:text-base">
                  {currentLang === 'ru' ? 'История входов в систему за последние 30 дней' : 'System login history for the last 30 days'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                <Link to="/"
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 sm:px-4 py-2 rounded-md transition-all duration-200 flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>{currentLang === 'ru' ? 'На главную' : 'Home'}</span>
                </Link>
                <button
                  onClick={fetchLogs}
                  disabled={loading}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 sm:px-4 py-2 rounded-md transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{loading ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') : (currentLang === 'ru' ? 'Обновить' : 'Refresh')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Навигация по вкладкам */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-4 sm:space-x-8 px-3 sm:px-4 lg:px-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('logs')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                  activeTab === 'logs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {currentLang === 'ru' ? 'Логи авторизаций' : 'Login Logs'}
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                  activeTab === 'stats'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {currentLang === 'ru' ? 'Статистика' : 'Statistics'}
              </button>
            </nav>
          </div>

          {/* Содержимое вкладок */}
          <div className="p-3 sm:p-4 lg:p-6">
            {activeTab === 'logs' && (
              <>
                {/* Фильтры */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                    {currentLang === 'ru' ? 'Фильтры' : 'Filters'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {/* Статус авторизации */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {currentLang === 'ru' ? 'Статус' : 'Status'}
                      </label>
                      <select
                        value={filters.success}
                        onChange={(e) => handleFilterChange('success', e.target.value)}
                        className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                      >
                        <option value="">{currentLang === 'ru' ? 'Все' : 'All'}</option>
                        <option value="1">{currentLang === 'ru' ? 'Успешно' : 'Success'}</option>
                        <option value="0">{currentLang === 'ru' ? 'Неуспешно' : 'Failed'}</option>
                      </select>
                    </div>
                    
                    {/* IP адрес */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        IP адрес
                      </label>
                      <input
                        type="text"
                        value={filters.ip}
                        onChange={(e) => handleFilterChange('ip', e.target.value)}
                        className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                        placeholder={currentLang === 'ru' ? 'Введите IP' : 'Enter IP'}
                      />
                    </div>
                    
                    {/* Дата с */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {currentLang === 'ru' ? 'Дата с' : 'Date from'}
                      </label>
                      <input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => handleFilterChange('date_from', e.target.value)}
                        className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                      />
                    </div>
                    
                    {/* Дата по */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {currentLang === 'ru' ? 'Дата по' : 'Date to'}
                      </label>
                      <input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => handleFilterChange('date_to', e.target.value)}
                        className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                      />
                    </div>
                  </div>
                  
                  {/* Кнопка очистки фильтров */}
                  <div className="mt-3 sm:mt-4 flex justify-end">
                    <button
                      onClick={clearFilters}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                    >
                      {currentLang === 'ru' ? 'Очистить фильтры' : 'Clear filters'}
                    </button>
                  </div>
                </div>

                {/* Статистика */}
                <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                    <div>
                      <span className="text-xs sm:text-sm text-gray-600">
                        {currentLang === 'ru' ? 'Всего записей:' : 'Total records:'} {totalLogs}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs sm:text-sm text-gray-600">
                        {currentLang === 'ru' ? 'Страница' : 'Page'} {currentPage} {currentLang === 'ru' ? 'из' : 'of'} {totalPages}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ошибка */}
                {error && (
                  <div className="mb-4 p-3 sm:p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Таблица логов */}
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              #
                            </th>
                            <th className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Дата и время' : 'Date & Time'}</span>
                              <span className="sm:hidden">{currentLang === 'ru' ? 'Дата' : 'Date'}</span>
                            </th>
                            <th className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              IP адрес
                            </th>
                            <th className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Статус' : 'Status'}</span>
                              <span className="sm:hidden">{currentLang === 'ru' ? 'Стат.' : 'Status'}</span>
                            </th>
                            <th className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <span className="hidden lg:inline">{currentLang === 'ru' ? 'User Agent' : 'User Agent'}</span>
                              <span className="hidden sm:inline lg:hidden">{currentLang === 'ru' ? 'User Agent' : 'UA'}</span>
                              <span className="sm:hidden">{currentLang === 'ru' ? 'UA' : 'UA'}</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getCurrentPageData().map((log, index) => (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-2 sm:px-3 lg:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                                {(currentPage - 1) * limit + index + 1}
                              </td>
                              <td className="px-2 sm:px-3 lg:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                                <span className="hidden sm:inline">{formatDate(log.datetime)}</span>
                                <span className="sm:hidden">{formatDate(log.datetime).split(' ')[0]}</span>
                              </td>
                              <td className="px-2 sm:px-3 lg:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                                <code className="bg-gray-100 px-1 sm:px-2 py-1 rounded text-xs break-all">
                                  {log.ip || '-'}
                                </code>
                              </td>
                              <td className="px-2 sm:px-3 lg:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                                <span className={`px-1 sm:px-2 py-1 text-xs font-bold rounded-full ${getSuccessColor(log.success)}`}>
                                  <span className="hidden sm:inline">{getSuccessText(log.success)}</span>
                                  <span className="sm:hidden">
                                    {log.success === 1 ? (currentLang === 'ru' ? 'Усп.' : 'OK') : (currentLang === 'ru' ? 'Ошиб.' : 'Err')}
                                  </span>
                                </span>
                              </td>
                              <td className="px-2 sm:px-3 lg:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 max-w-xs">
                                <span title={log.user_agent || '-'} className="break-words">
                                  <span className="hidden lg:inline">{shortenUserAgent(log.user_agent)}</span>
                                  <span className="hidden sm:inline lg:hidden">{shortenUserAgent(log.user_agent).substring(0, 30)}...</span>
                                  <span className="sm:hidden">{shortenUserAgent(log.user_agent).substring(0, 20)}...</span>
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Пагинация */}
                {totalPages > 1 && (
                  <div className="bg-white px-3 sm:px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 sm:mt-6 rounded-lg shadow-md">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        {currentLang === 'ru' ? 'Предыдущая' : 'Previous'}
                      </button>
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="ml-2 sm:ml-3 relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        {currentLang === 'ru' ? 'Следующая' : 'Next'}
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-700">
                          {currentLang === 'ru' ? 'Показано' : 'Showing'} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {currentLang === 'ru' ? 'до' : 'to'} <span className="font-medium">{Math.min(currentPage * limit, totalLogs)}</span> {currentLang === 'ru' ? 'из' : 'of'} <span className="font-medium">{totalLogs}</span> {currentLang === 'ru' ? 'результатов' : 'results'}
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <span className="sr-only">{currentLang === 'ru' ? 'Предыдущая' : 'Previous'}</span>
                            <svg className="h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                          
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`relative inline-flex items-center px-3 sm:px-4 py-2 border text-xs sm:text-sm font-medium ${
                                  page === currentPage
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          })}
                          
                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <span className="sr-only">{currentLang === 'ru' ? 'Следующая' : 'Next'}</span>
                            <svg className="h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'stats' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Карточки статистики */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <p className="text-xs sm:text-sm font-medium text-gray-500">
                          {currentLang === 'ru' ? 'Всего попыток' : 'Total Attempts'}
                        </p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.total}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <p className="text-xs sm:text-sm font-medium text-gray-500">
                          {currentLang === 'ru' ? 'Успешных' : 'Successful'}
                        </p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.successful}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <p className="text-xs sm:text-sm font-medium text-gray-500">
                          {currentLang === 'ru' ? 'Неуспешных' : 'Failed'}
                        </p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.failed}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <p className="text-xs sm:text-sm font-medium text-gray-500">
                          {currentLang === 'ru' ? 'Процент успеха' : 'Success Rate'}
                        </p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.successRate}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* График или дополнительная статистика */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
                  <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                    {currentLang === 'ru' ? 'Дополнительная информация' : 'Additional Information'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {currentLang === 'ru' ? 'Период:' : 'Period:'} {currentLang === 'ru' ? 'Последние 30 дней' : 'Last 30 days'}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {currentLang === 'ru' ? 'Среднее попыток в день:' : 'Average attempts per day:'} {Math.round(stats.total / 30)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {currentLang === 'ru' ? 'Уникальных IP:' : 'Unique IPs:'} {new Set(allLogs.map(log => log.ip).filter(ip => ip)).size}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginLogs; 